import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

export default function AdminVoteModal({ participant, onRefresh, onClose }) {
    const [others, setOthers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [noteDrafts, setNoteDrafts] = useState({});
    const [savingStatus, setSavingStatus] = useState({});
    const timeouts = React.useRef({});

    const fetchOthers = useCallback(async () => {
        if (!participant || !participant.token) return;
        try {
            setLoading(true);
            const res = await axios.get(`/speeddate/api/participants/others?token=${participant.token}`);
            setOthers(res.data);

            const drafts = {};
            res.data.forEach(o => {
                if (o.ReceivedVotes?.[0]?.note) {
                    drafts[o.id] = o.ReceivedVotes[0].note;
                }
            });
            setNoteDrafts(drafts);
        } catch (err) {
            console.error("Error fetching others for admin vote:", err);
        } finally {
            setLoading(false);
        }
    }, [participant]);

    useEffect(() => {
        fetchOthers();
        return () => {
            Object.values(timeouts.current).forEach(clearTimeout);
        };
    }, [fetchOthers]);

    const handleVote = async (targetId, choice, isMagic = false, note = undefined) => {
        const currentDraft = noteDrafts[targetId] || '';
        const finalNote = note !== undefined ? note : currentDraft;

        if (note !== undefined) {
            setSavingStatus(prev => ({ ...prev, [targetId]: 'saving' }));
        }

        try {
            await axios.post('/speeddate/api/participants/vote', {
                token: participant.token,
                targetId,
                choice,
                note: finalNote,
                isMagic
            });

            if (note !== undefined) {
                setSavingStatus(prev => ({ ...prev, [targetId]: 'saved' }));
                setTimeout(() => {
                    setSavingStatus(prev => ({ ...prev, [targetId]: null }));
                }, 2000);
            }

            if (isMagic) {
                await fetchOthers();
            } else {
                setOthers(prev => prev.map(o => {
                    if (o.id === targetId) {
                        return {
                            ...o,
                            ReceivedVotes: [{
                                ...o.ReceivedVotes?.[0],
                                choice,
                                note: finalNote,
                                isMagic: isMagic
                            }]
                        };
                    }
                    return o;
                }));
            }
            if (onRefresh) onRefresh();
        } catch (err) {
            console.error("Error voting as admin:", err);
            if (note !== undefined) setSavingStatus(prev => ({ ...prev, [targetId]: null }));
        }
    };

    const handleNoteChange = (targetId, val) => {
        setNoteDrafts(prev => ({ ...prev, [targetId]: val }));

        if (timeouts.current[targetId]) {
            clearTimeout(timeouts.current[targetId]);
        }

        setSavingStatus(prev => ({ ...prev, [targetId]: 'typing' }));

        timeouts.current[targetId] = setTimeout(() => {
            const targetParticipant = others.find(o => o.id === targetId);
            const vote = targetParticipant?.ReceivedVotes?.[0];
            handleVote(targetId, vote?.choice ?? false, vote?.isMagic ?? false, val);
        }, 1500);
    };

    return (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 1100 }}>
            <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
                <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '25px' }}>
                    <div className="modal-header border-0 pt-4 px-4 bg-primary text-white" style={{ borderTopLeftRadius: '25px', borderTopRightRadius: '25px' }}>
                        <div>
                            <h5 className="modal-title fw-bold">Inserimento Preferenze</h5>
                            <small className="opacity-75">Stai votando per: <strong>{participant.firstName} {participant.lastName}</strong> (#{participant.arrivalNumber})</small>
                        </div>
                        <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
                    </div>
                    <div className="modal-body p-4 bg-light bg-opacity-50">
                        {loading ? (
                            <div className="text-center py-5">
                                <div className="spinner-border text-primary" role="status"></div>
                                <p className="mt-2 text-muted">Caricamento partecipanti...</p>
                            </div>
                        ) : others.length === 0 ? (
                            <div className="text-center py-5">
                                <i className="fas fa-users-slash fa-3x text-muted mb-3 opacity-20"></i>
                                <p className="text-muted">Nessun partner disponibile per il voto.</p>
                            </div>
                        ) : (
                            <div className="row g-3">
                                {others.map(other => {
                                    const vote = other.ReceivedVotes?.[0];
                                    const currentChoice = vote?.choice;
                                    const currentMagic = vote?.isMagic;
                                    const currentNote = noteDrafts[other.id] || '';

                                    return (
                                        <div className="col-12" key={other.id}>
                                            <div className={`card border-0 shadow-sm rounded-4 p-3 ${currentMagic ? 'border-start border-warning border-4' : ''}`}>
                                                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3">
                                                    <div className="d-flex align-items-center">
                                                        <div className="bg-light text-primary rounded-circle fw-bold d-flex align-items-center justify-content-center me-3" style={{ width: '40px', height: '40px' }}>
                                                            {other.arrivalNumber}
                                                        </div>
                                                        <div>
                                                            <h6 className="fw-bold mb-0">{other.firstName} {other.lastName}</h6>
                                                            <small className="text-muted">
                                                                {other.birthDate ? `${new Date().getFullYear() - new Date(other.birthDate).getFullYear()} anni` : ''}
                                                            </small>
                                                        </div>
                                                    </div>
                                                    <div className="d-flex gap-2">
                                                        <button
                                                            className={`btn btn-sm rounded-pill px-3 fw-bold ${currentChoice === true ? 'btn-success' : 'btn-outline-success opacity-50'}`}
                                                            onClick={() => handleVote(other.id, true, currentMagic)}
                                                        >
                                                            SI
                                                        </button>
                                                        <button
                                                            className={`btn btn-sm rounded-pill px-3 fw-bold ${currentChoice === false ? 'btn-danger' : 'btn-outline-danger opacity-50'}`}
                                                            onClick={() => handleVote(other.id, false, false)}
                                                        >
                                                            NO
                                                        </button>
                                                        <button
                                                            className={`btn btn-sm rounded-pill px-3 fw-bold ${currentMagic ? 'btn-warning' : 'btn-outline-warning opacity-50'}`}
                                                            onClick={() => handleVote(other.id, true, !currentMagic)}
                                                        >
                                                            <i className="fas fa-star me-1"></i> MAGIC
                                                        </button>
                                                    </div>
                                                </div>
                                                <div className="mt-3">
                                                    <div className="input-group input-group-sm">
                                                        <span className="input-group-text bg-white border-end-0 text-muted"><i className="fas fa-pencil-alt"></i></span>
                                                        <input
                                                            type="text"
                                                            className="form-control border-start-0 ps-0"
                                                            placeholder="Nota..."
                                                            value={currentNote}
                                                            onChange={(e) => handleNoteChange(other.id, e.target.value)}
                                                        />
                                                        {savingStatus[other.id] && (
                                                            <span className="input-group-text bg-white">
                                                                {savingStatus[other.id] === 'typing' || savingStatus[other.id] === 'saving' ? (
                                                                    <i className="fas fa-sync fa-spin text-muted"></i>
                                                                ) : (
                                                                    <i className="fas fa-check text-success"></i>
                                                                )}
                                                            </span>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}
